import { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import useScrollToTop from '../ScrollToTop/useScrollToTop';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobOpen, setMobOpen]   = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobDrop, setMobDrop]   = useState(false);
  const dropRef  = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const handleNav = useScrollToTop();

  // Pages where navbar should be dark always (not transparent)
  const darkPages = ['/about'];
  const isDarkPage = darkPages.includes(location.pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const close = () => setMobOpen(false);

  const mobNav = (path) => {
    close();
    handleNav(path);
  };

  const galleries = [
    { label: 'Wedding Photography',   path: '/wedding' },
    { label: 'Cinematic Videography', path: '/cinematic' },
    { label: 'Baby & NewBorn Shoot',  path: '/babyshoot' },
    { label: 'Maternity Photography', path: '/maternity' },
    { label: 'Pre-wedding Shoot',     path: '/prewedding' },
    { label: 'Bharathanatyam Shoot',  path: '/bharatanatyam' },
    { label: 'Corporate Events',      path: '/corporate' },
  ];

  // Navbar class — dark page la always dark, else scroll-based
  const navClass = `navbar ${scrolled || isDarkPage ? 'scrolled' : ''}`;

  return (
    <>
      {/* ── Backdrop overlay ── */}
      <div className={`mob-overlay ${mobOpen ? 'open' : ''}`} onClick={close} />

      {/* ── Side Drawer ── */}
      <div className={`mob-drawer ${mobOpen ? 'open' : ''}`}>

        {/* Header: Logo + Close */}
        <div className="mob-drawer-header">
          <button className="mob-drawer-logo" onClick={() => mobNav('/')}>
            <div className="mob-drawer-logo-main">
              <span className="w">BRAND</span>
              <span className="r">PARTNER</span>
            </div>
            <div className="mob-drawer-logo-tag">Studio and Gifts</div>
          </button>
          <button className="mob-close" onClick={close} aria-label="Close menu">✕</button>
        </div>

        {/* Social Icons */}
        <div className="mob-socials">
          <a href="https://www.instagram.com/brandpartnersphotography/" target="_blank" rel="noreferrer" aria-label="Instagram">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="2" width="20" height="20" rx="5"/>
              <circle cx="12" cy="12" r="4"/>
              <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
            </svg>
          </a>
          <a href="https://www.youtube.com/@brandpartners5095" target="_blank" rel="noreferrer" aria-label="YouTube">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/>
              <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"/>
            </svg>
          </a>
          <a href="https://wa.me/919600998798" target="_blank" rel="noreferrer" aria-label="WhatsApp">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
          </a>
        </div>

        {/* Nav Links */}
        <nav className="mob-links">
          <button onClick={() => mobNav('/')}>Home</button>
          <button onClick={() => mobNav('/about')}>About</button>
          <button onClick={() => mobNav('/services')}>Services</button>

          {/* Portfolio: label navigates, chevron toggles accordion */}
          <div className="mob-portfolio-row">
            <button className="mob-portfolio-link" onClick={() => mobNav('/portfolio')}>
              PORTFOLIO
            </button>
            <button
              className={`mob-chevron-btn ${mobDrop ? 'open' : ''}`}
              onClick={() => setMobDrop(!mobDrop)}
              aria-label="Toggle portfolio galleries"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
          </div>

          {mobDrop && (
            <div className="mob-drop-list">
              {galleries.map((g) => (
                <button key={g.path} onClick={() => mobNav(g.path)}>
                  {g.label}
                </button>
              ))}
            </div>
          )}
        </nav>

        {/* CTA */}
        <div className="mob-drawer-cta">
          <button onClick={() => mobNav('/contact')}>Book a Session</button>
        </div>

      </div>

      {/* ── Navbar ── */}
      <nav className={navClass}>

        <button className="nav-logo" onClick={() => handleNav('/')}>
          <div className="logo-text-wrap">
            <div className="logo-main-line">
              <span className="logo-brand">BRAND</span>
              <span className="logo-partner">PARTNERS</span>
            </div>
            <div className="logo-tagline">PHOTOGRAPHY</div>
          </div>
        </button>

        <ul className="nav-center">
          <li><button onClick={() => handleNav('/')}>Home</button></li>
          <li><button onClick={() => handleNav('/about')}>About</button></li>
          <li><button onClick={() => handleNav('/services')}>Services</button></li>

          {/* Portfolio: word = navigate, chevron = dropdown */}
          <li className="has-drop" ref={dropRef}>
            <div className="drop-trigger-group">
              <span
                className="drop-label"
                onClick={() => { handleNav('/portfolio'); setDropOpen(false); }}
              >
                Portfolio
              </span>
              <button
                className="drop-chevron"
                onClick={(e) => { e.stopPropagation(); setDropOpen(!dropOpen); }}
                aria-expanded={dropOpen}
                aria-label="Toggle portfolio dropdown"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                  style={{ transition: 'transform 0.25s', transform: dropOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
            </div>

            <ul className={`drop-menu ${dropOpen ? 'open' : ''}`}>
              {galleries.map((g) => (
                <li key={g.path}>
                  <button onClick={() => { handleNav(g.path); setDropOpen(false); }}>
                    {g.label}
                  </button>
                </li>
              ))}
            </ul>
          </li>
        </ul>

        <div className="nav-cta">
          <button className="nav-btn" onClick={() => handleNav('/contact')}>Contact</button>
          <button
            className={`hamburger ${mobOpen ? 'active' : ''}`}
            onClick={() => setMobOpen(!mobOpen)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>
    </>
  );
}